// Plurals and placeholders in a phrase, for any language.
//
// Plural syntax is ICU's: "{n, plural, =0 {Nobody} one {# queue} few {# queues} other {# queues}}".
// The branch is chosen by an exact =N, then Intl.PluralRules' category, then other; # is the number.
// Legacy "a|b|c" forms (az/ru written before this) still read: one|other, or one|few|many.

export type Vars = Record<string, string | number>

const rules = new Map<string, Intl.PluralRules | null>()

/** CLDR category of a whole number when the runtime has no Intl data for lang (matches internal/i18n/plural.go). */
function builtInCategory(lang: string, n: number): Intl.LDMLPluralRule {
  const i = Math.abs(Math.trunc(n))
  const last = i % 10
  const lastTwo = i % 100
  const fewish = last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)
  switch (lang) {
    case 'ru':
      return last === 1 && lastTwo !== 11 ? 'one' : fewish ? 'few' : 'many'
    case 'pl':
      return i === 1 ? 'one' : fewish ? 'few' : 'many'
    case 'fil':
      return last !== 4 && last !== 6 && last !== 9 ? 'one' : 'other'
    case 'fr':
      return i === 0 || i === 1 ? 'one' : 'other'
    default:
      return i === 1 ? 'one' : 'other'
  }
}

/** The plural category of n in lang, from Intl.PluralRules where the runtime knows lang. */
export function pluralCategory(lang: string, n: number): Intl.LDMLPluralRule {
  let rule = rules.get(lang)
  if (rule === undefined) {
    try {
      rule = Intl.PluralRules.supportedLocalesOf([lang]).length ? new Intl.PluralRules(lang) : null
    } catch {
      rule = null
    }
    rules.set(lang, rule)
  }
  return rule ? rule.select(n) : builtInCategory(lang, n)
}

/** Index into legacy "a|b|c" forms: one|other, or one|few|many(other). */
export function legacyIndex(lang: string, n: number, forms: number): number {
  if (forms <= 1) return 0
  const cat = pluralCategory(lang, Math.abs(Math.trunc(n)))
  if (forms === 2) return cat === 'one' ? 0 : 1
  return cat === 'one' ? 0 : cat === 'few' ? 1 : 2
}

/** The brace closing the one at start, or -1. */
function closing(text: string, start: number): number {
  let depth = 0
  for (let i = start; i < text.length; i++) {
    if (text[i] === '{') depth++
    else if (text[i] === '}' && --depth === 0) return i
  }
  return -1
}

function branches(body: string): Map<string, string> {
  const out = new Map<string, string>()
  let rest = body
  for (;;) {
    const brace = rest.indexOf('{')
    if (brace < 0) return out
    const end = closing(rest, brace)
    if (end < 0) return out
    out.set(rest.slice(0, brace).trim(), rest.slice(brace + 1, end))
    rest = rest.slice(end + 1)
  }
}

const PLURAL_OPEN = /\{(\w+),\s*plural,/

function countOf(vars: Vars | undefined, name: string): number {
  const own = vars?.[name]
  if (typeof own === 'number') return own
  if (typeof vars?.count === 'number') return vars.count
  const parsed = Number(String(own ?? '').replace(/[^\d.-]/g, ''))
  return Number.isFinite(parsed) ? parsed : 0
}

/** Each ICU plural block resolved for its variable. */
function resolvePlurals(text: string, lang: string, vars: Vars | undefined): string {
  let out = text
  for (let m = PLURAL_OPEN.exec(out); m; m = PLURAL_OPEN.exec(out)) {
    const end = closing(out, m.index)
    if (end < 0) break
    const name = m[1]!
    const n = countOf(vars, name)
    const forms = branches(out.slice(m.index + m[0].length, end))
    const chosen = forms.get(`=${n}`) ?? forms.get(pluralCategory(lang, n)) ?? forms.get('other') ?? ''
    const shown = vars && name in vars ? String(vars[name]) : String(n)
    out = out.slice(0, m.index) + chosen.replace(/#/g, shown) + out.slice(end + 1)
  }
  return out
}

/** `{name}` replaced by vars.name. One not given stays as written, so the gap is visible. */
function fill(text: string, vars: Vars | undefined): string {
  if (!vars) return text
  return text.replace(/\{(\w+)\}/g, (whole, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : whole,
  )
}

/**
 * A phrase said: plural forms chosen, placeholders filled.
 * Legacy pipe forms count by vars.count, else vars.n.
 */
export function formatPhrase(text: string, lang: string, vars?: Vars): string {
  if (PLURAL_OPEN.test(text)) return fill(resolvePlurals(text, lang, vars), vars)
  if (text.includes('|')) {
    const forms = text.split('|')
    const n = typeof vars?.count === 'number' ? vars.count : typeof vars?.n === 'number' ? vars.n : 0
    return fill(forms[legacyIndex(lang, n, forms.length)] ?? forms[0]!, vars)
  }
  return fill(text, vars)
}

/** The names a phrase fills: {name} and the variable of each {name, plural, …}. */
export function placeholdersIn(text: string): string[] {
  const names = [...text.matchAll(/\{(\w+)(?:\}|,\s*plural,)/g)].map((m) => m[1]!)
  return [...new Set(names)].sort()
}

/** The names a phrase prints: each {name}, and a plural's variable where a branch prints it as #. */
export function shownPlaceholdersIn(text: string): string[] {
  // Inside a plural or select, a branch of plain words ("one {once}") is not a placeholder.
  const blocks = [...text.matchAll(/\{\w+,\s*(?:plural|select),/g)].map((m) => [m.index!, closing(text, m.index!)] as const)
  const depth = (from: number, to: number) => [...text.slice(from, to)].reduce((d, c) => d + (c === '{' ? 1 : c === '}' ? -1 : 0), 0)
  const names = [...text.matchAll(/\{(\w+)\}/g)]
    .filter((m) => {
      const inner = blocks.filter(([from, to]) => m.index! > from && m.index! < to).at(-1)
      return !inner || depth(inner[0], m.index!) !== 1
    })
    .map((m) => m[1]!)
  if (text.includes('#')) names.push(...[...text.matchAll(/\{(\w+),\s*plural,/g)].map((m) => m[1]!))
  return [...new Set(names)].sort()
}
